import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Search, Filter, Heart, Users, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import NeedPostCard from '../components/NeedPostCard';
import { BannerAdLarge, BannerAdInline } from '../components/BannerAd';
import { RulesAlertBanner } from '../components/ContentRules';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Feed = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [categories, setCategories] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [platformStats, setPlatformStats] = useState({ total_users: 0, total_raised: 0 });
  const observerRef = useRef();
  const POSTS_PER_PAGE = 12;

  const fetchPosts = useCallback(async (pageNum = 0, reset = false) => {
    try {
      if (reset) setLoading(true);
      const params = { 
        skip: pageNum * POSTS_PER_PAGE, 
        limit: POSTS_PER_PAGE 
      };
      if (category !== 'all') params.category = category;
      
      const response = await axios.get(`${API_URL}/api/posts`, { params });
      const newPosts = response.data;
      
      if (reset) {
        setPosts(newPosts);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
      }
      
      setHasMore(newPosts.length === POSTS_PER_PAGE);
    } catch { /* ignore */ } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category]);

  const loadMorePosts = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setPage(prev => {
      const nextPage = prev + 1;
      fetchPosts(nextPage);
      return nextPage;
    });
  }, [loadingMore, hasMore, fetchPosts]);

  // Intersection Observer for infinite scroll
  const lastPostRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMorePosts();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, loadMorePosts]);

  useEffect(() => {
    setPosts([]);
    setPage(0);
    setHasMore(true);
    fetchPosts(0, true);
    fetchCategories();
    fetchPlatformStats();
  }, [category, fetchPosts]);

  const fetchPlatformStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats`);
      setPlatformStats(response.data);
    } catch { /* ignore */ }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch { /* ignore */ }
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    if (cat) {
      return language === 'it' ? cat.name_it : cat.name_en;
    }
    return catId;
  };

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        {/* Rules Alert Banner */}
        <RulesAlertBanner />

        {/* Social Proof Banner */}
        {platformStats && platformStats.total_donations > 0 && (
          <div className="bg-gradient-to-r from-emerald-700 to-emerald-800 rounded-2xl p-4 mb-6 flex items-center justify-between text-white overflow-hidden relative" data-testid="social-proof-banner">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-600/20 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center animate-pulse">
                <Heart className="h-5 w-5 text-amber-300" fill="currentColor" />
              </div>
              <div>
                <p className="font-semibold text-sm">
                  {language === 'it' ? `${platformStats.total_donations} donazioni effettuate` : `${platformStats.total_donations} donations made`}
                </p>
                <p className="text-emerald-200 text-xs">
                  {language === 'it' ? `€${platformStats.total_raised?.toLocaleString() || 0} raccolti dalla community` : `€${platformStats.total_raised?.toLocaleString() || 0} raised by the community`}
                </p>
              </div>
            </div>
            <div className="text-right relative z-10">
              <p className="text-2xl font-heading font-bold">{platformStats.total_users}</p>
              <p className="text-emerald-200 text-xs">{language === 'it' ? 'utenti attivi' : 'active users'}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">
              {language === 'it' ? 'Tutte le Richieste di Aiuto' : 'All Help Requests'}
            </h1>
            <p className="text-stone-600 mt-0.5 text-sm sm:text-base">
              {language === 'it' 
                ? 'Scorri e scopri chi ha bisogno del tuo aiuto' 
                : 'Scroll and discover who needs your help'}
            </p>
          </div>
          
          {isAuthenticated && (
            <Link to="/create-post">
              <Button
                data-testid="create-post-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6 py-3 font-semibold shadow-lg shadow-amber-500/30"
              >
                <Plus className="h-5 w-5 mr-2" />
                {language === 'it' ? 'Racconta la tua storia' : 'Share your story'}
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
              <Input
                data-testid="search-input"
                placeholder={language === 'it' ? 'Cerca tra le richieste...' : 'Search requests...'}
                className="pl-10 h-12 rounded-xl border-stone-200 bg-stone-50 focus:bg-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="category-filter" className="w-full md:w-[220px] h-12 rounded-xl border-stone-200">
                <Filter className="h-4 w-4 mr-2 text-stone-400" />
                <SelectValue placeholder={t('category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {language === 'it' ? cat.name_it : cat.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-emerald-800 rounded-2xl p-3 mb-6 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-white">
          <div className="text-center">
            <p className="font-heading text-2xl font-bold" data-testid="feed-stat-users">{platformStats.total_users || 0}</p>
            <p className="text-emerald-200 text-sm">{language === 'it' ? 'Utenti registrati' : 'Registered users'}</p>
          </div>
          <div className="w-px h-10 bg-emerald-700 hidden sm:block" />
          <div className="text-center">
            <p className="font-heading text-2xl font-bold">{platformStats.active_posts || filteredPosts.length}+</p>
            <p className="text-emerald-200 text-sm">{language === 'it' ? 'Richieste attive' : 'Active requests'}</p>
          </div>
          <div className="w-px h-10 bg-emerald-700 hidden sm:block" />
          <div className="text-center">
            <p className="font-heading text-2xl font-bold">
              €{(platformStats.total_raised || filteredPosts.reduce((acc, p) => acc + p.amount_raised, 0)).toFixed(0)}
            </p>
            <p className="text-emerald-200 text-sm">{language === 'it' ? 'Raccolti' : 'Raised'}</p>
          </div>
          <div className="w-px h-10 bg-emerald-700 hidden sm:block" />
          <div className="text-center">
            <p className="font-heading text-2xl font-bold">
              {platformStats.total_donations || filteredPosts.reduce((acc, p) => acc + p.donors_count, 0)}
            </p>
            <p className="text-emerald-200 text-sm">{language === 'it' ? 'Donazioni' : 'Donations'}</p>
          </div>
        </div>

        {/* Posts Grid with Infinite Scroll */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-stone-700 mb-2">
              {language === 'it' ? 'Nessuna richiesta trovata' : 'No requests found'}
            </h3>
            <p className="text-stone-500 mb-6">
              {language === 'it' 
                ? 'Sii il primo a condividere la tua storia!' 
                : 'Be the first to share your story!'}
            </p>
            {isAuthenticated && (
              <Link to="/create-post">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6">
                  <Plus className="h-5 w-5 mr-2" />
                  {language === 'it' ? 'Crea la tua richiesta' : 'Create your request'}
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Banner pubblicitario sopra i post */}
            <div className="mb-6">
              <BannerAdLarge index={0} language={language} />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, index) => {
                const isLast = index === filteredPosts.length - 1;
                
                // Inserisci un banner inline ogni 6 post
                const shouldShowAd = (index + 1) % 6 === 0 && index < filteredPosts.length - 1;
                
                return (
                  <React.Fragment key={post.post_id}>
                    <motion.div
                      ref={isLast ? lastPostRef : null}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.05, 0.5) }}
                    >
                      <NeedPostCard post={post} getCategoryName={getCategoryName} />
                    </motion.div>
                    
                    {shouldShowAd && (
                      <motion.div 
                        className="md:col-span-2 lg:col-span-3"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <BannerAdInline index={Math.floor(index / 6)} language={language} />
                      </motion.div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            
            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-800" />
                <span className="ml-2 text-stone-600">
                  {language === 'it' ? 'Caricamento...' : 'Loading...'}
                </span>
              </div>
            )}
            
            {/* End of List */}
            {!hasMore && filteredPosts.length > 0 && (
              <div className="text-center py-8 text-stone-500">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
                <p>{language === 'it' ? 'Hai visto tutte le richieste!' : 'You\'ve seen all requests!'}</p>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Feed;
